import React, { useState } from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import { CheckIcon } from '@heroicons/react/24/outline';

const ApproveAllReturnsModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isLoading = false,
  entity 
}) => {
  const [approvalComments, setApprovalComments] = useState('');

  const orderItems = entity?.orderItems || [];
  const returnRequestedItems = orderItems.filter(item => 
    item.status === 'return_requested_by_customer'
  );

  const handleConfirm = () => {
    onConfirm({ approvalComments });
  };

  const handleClose = () => {
    setApprovalComments('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Approve Returns"
      size="lg"
    >
      <div className="py-4">
        <div className="flex items-center mb-4">
          <CheckIcon className="h-12 w-12 text-green-500 mr-4" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Approve Return Requests
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              This will approve request in this fulfillment group.
            </p>
          </div>
        </div>

        {returnRequestedItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No return requests found to approve.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Items to be approved */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Items to be approved ({returnRequestedItems.length}):
              </h4>
              
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                {returnRequestedItems.map((item) => (
                  <div key={item.id} className="p-3">
                    <div className="flex items-center space-x-3">
                      {item.product?.images?.[0] && (
                        <img
                          src={item.product.images[0].url}
                          alt={item.product.name}
                          className="h-12 w-12 object-cover rounded-md"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {item.product?.name || 'Product'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Size: {item.size} • Quantity: {item.quantity}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          ${item.price}
                        </p>
                        {item.returnRequest?.reason && (
                          <p className="text-sm text-gray-600 mt-1">
                            Reason: {item.returnRequest.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Comments */}
            <div>
              <label htmlFor="approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
                Approval comments (optional)
              </label>
              <textarea
                id="approval-comments"
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                placeholder="Add any comments about the approval..."
              />
            </div>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 mt-4">
          <h4 className="text-sm font-medium text-green-900 mb-2">
            What happens next:
          </h4>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• All return requests will be approved</li>
            <li>• Customers will be notified and receive return shipping instructions</li>
            <li>• Return labels will be generated automatically</li>
            <li>• You'll be notified when items are shipped back</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <Button
            onClick={handleConfirm}
            disabled={isLoading || returnRequestedItems.length === 0}
            isLoading={isLoading}
            variant="primary"
            className="flex items-center justify-center"
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            Approve Returns ({returnRequestedItems.length})
          </Button>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant="secondary"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ApproveAllReturnsModal; 